import React, { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import Card from 'transfix-ui/components/Card'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { Flag } from 'transfix-ui/core/icons'
import { ICarrierState } from 'types'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { IStore } from 'apps/carrier/utils/reducers'
import { OpenInNewTabIcon } from 'transfix-icons'
import {
  Button,
  INLINE_MESSAGE_VARIANTS,
  InlineMessage,
  brandColors,
  useGlobalToast,
} from 'transfix-ui'
import { useUserDetails } from 'utils/hooks'
import { CollapsibleSection } from '../../CollapsibleSection'
import { LoadableValue } from '../../LoadableValue'
import { CarrierStateTag } from './CarrierStateTag'
import { RulesAssessmentCard } from './RulesAssessmentCard'
import { RulesManualReviewModal } from './RulesManualReviewModal'
import { TRANSFIX_RULE_KEYS } from './constants'
import { useCarrierStatusQuery } from './graphql/CarrierStatus'
import { useMotorCarrierComplianceFlagsQuery } from './graphql/MotorCarrierComplianceFlags'
import { useRefreshHighwayDataMutation } from './graphql/RefreshHighwayData'
import { IRulesAssessmentFragment } from './graphql/RulesAssessmentFragment'
import { useTransfixRulesAssessmentQuery } from './graphql/TransfixRulesAssessment'
import { sortRules } from './utils'

export const CarrierStatus = memo(() => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const isStagingRulesDebuggerEnabled = useFeatureFlag('stagingRulesDebugger')

  const openModalHandler = () => setIsModalOpen(true)
  const closeModalHandler = () => setIsModalOpen(false)

  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id: carrierApplicationId = '' } = carrierDetails || {}

  const [refreshHighData] = useRefreshHighwayDataMutation()
  const { openToast } = useGlobalToast()
  const {
    data: highwayRulesData,
    error: highwayRulesError,
    loading: highwayRulesLoading,
    refetch: highwayRulesRefetch,
  } = useCarrierStatusQuery({ variables: { carrierApplicationId } })

  const {
    data: transfixRulesData,
    error: transfixRulesError,
    loading: transfixRulesLoading,
    refetch: transfixRulesRefetch,
  } = useTransfixRulesAssessmentQuery({ variables: { carrierApplicationId } })

  const {
    data: motorCarrierComplianceFlagsData,
    loading: motorCarrierComplianceFlagsLoading,
    refetch: motorCarrierComplianceFlagsRefetch,
  } = useMotorCarrierComplianceFlagsQuery({
    variables: {
      carrierApplicationId,
    },
  })

  const refreshHighwayDataHandler = () => {
    refreshHighData({
      variables: {
        input: {
          carrierApplicationId,
        },
      },
    })
      .then(({ data }) => {
        if (data?.refreshHighwayData?.success) {
          openToast({
            toastMessage: 'Successfully synced',
          })
        }
      })
      .finally(() => {
        motorCarrierComplianceFlagsRefetch()
        transfixRulesRefetch()
        highwayRulesRefetch()
      })
  }

  const { motorCarrierComplianceFlags } = motorCarrierComplianceFlagsData || {}
  const { highwayCarrier } = highwayRulesData || {}
  const { dotNumber, highwayId, isMonitored, mcNumber, rulesAssessments: highwayRules = [] } =
    highwayCarrier || {}

  const { motorCarrierTransfixRulesAssessments } = transfixRulesData || {}
  const { userPermissions } = useUserDetails()
  const { canEditCarrierStatus, canEditComplianceCarrierInfo } = userPermissions || {}
  const isCarrierAppHighwayButtonSyncEnabled = useFeatureFlag('carrierAppHighwayButtonSync')

  const transfixRules: IRulesAssessmentFragment[] = (
    motorCarrierTransfixRulesAssessments || []
  )?.map(({ id, override, result, rule: { name } }) => ({
    override,
    ruleName: name,
    result,
    id,
  }))

  const allRules = [...highwayRules, ...transfixRules]
  const rules = sortRules(allRules)
  const loading = transfixRulesLoading || highwayRulesLoading
  const showDetailsFromOtherSources = !!(highwayId || mcNumber || dotNumber)

  const MonitoredByHighwayTag = () => (
    <Box display='flex' alignItems='center' mb='8px'>
      <Box
        height='12px'
        width='12px'
        mr='4px'
        mb='2px'
        borderRadius='100%'
        bgcolor={isMonitored ? brandColors.green2 : brandColors.coolGray4}
      />
      <Typography variant='caption'>
        Actively Monitored By Highway: {isMonitored ? 'On' : 'Off'}
      </Typography>
      {canEditComplianceCarrierInfo && isCarrierAppHighwayButtonSyncEnabled && (
        <Box ml={0.5} display='flex'>
          <Button color='tertiary' onClick={refreshHighwayDataHandler} noPadding>
            <Typography variant='caption'>Sync</Typography>
          </Button>
        </Box>
      )}
    </Box>
  )

  const DetailsFromOtherSources = () => (
    <div>
      {showDetailsFromOtherSources && (
        <Typography variant='caption' color='textSecondary'>
          View details from other sources
        </Typography>
      )}
      <Box display='grid' gap='8px' justifyItems='start' ml={-1} mt='16px'>
        {highwayId && (
          <Button
            variant='text'
            target='_blank'
            endIcon={<OpenInNewTabIcon size='large' />}
            href={`https://gohighway.com/broker/carriers/${highwayId}`}>
            Highway
          </Button>
        )}
        {mcNumber && (
          <Button
            variant='text'
            target='_blank'
            endIcon={<OpenInNewTabIcon size='large' />}
            href={`https://www.carrier411.com/manager/companydetail.cfm?docket=MC${mcNumber}`}>
            Carrier 411
          </Button>
        )}
        {dotNumber && (
          <Button
            variant='text'
            target='_blank'
            endIcon={<OpenInNewTabIcon size='large' />}
            href={`https://ai.fmcsa.dot.gov/SMS/Carrier/${dotNumber}/CompleteProfile.aspx`}>
            FMCSA
          </Button>
        )}
      </Box>
    </div>
  )

  const CarrierStatusFlag = () => {
    return (
      <LoadableValue
        value={
          <div>
            {motorCarrierComplianceFlags?.map(({ complianceFlag: { name } }) => (
              <Box display='flex' alignItems='center' gap='4px' key={name}>
                <Flag color='error' />
                <Typography variant='body1' color='error'>
                  {name === 'dnu' ? 'Do Not Use' : 'Probation'}
                </Typography>
              </Box>
            ))}
          </div>
        }
        loading={motorCarrierComplianceFlagsLoading}
      />
    )
  }

  return (
    <Card>
      <Box display='grid' gap='16px'>
        <CarrierStateTag />
        {!!motorCarrierComplianceFlags?.length && <CarrierStatusFlag />}
        <MonitoredByHighwayTag />
        {transfixRulesError && (
          <InlineMessage
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message={transfixRulesError.message}
          />
        )}
        {highwayRulesError && (
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={highwayRulesError.message} />
        )}
        {isStagingRulesDebuggerEnabled && (__ENV__ === 'staging' || __ENV__ === 'development') && (
          <Box maxWidth='318px' overflow='auto'>
            <Typography variant='caption'>
              <CollapsibleSection
                Title={() => <Typography color='error'>STAGING RULES DEBUGGER</Typography>}>
                <pre>
                  {JSON.stringify(
                    allRules.map(({ override, result, ruleName }) => ({
                      name: ruleName,
                      result,
                      override: override?.result || 'no override',
                      isTfxRule: TRANSFIX_RULE_KEYS.includes(ruleName),
                    })),
                    null,
                    2
                  )}
                </pre>
              </CollapsibleSection>
            </Typography>
          </Box>
        )}
        <RulesAssessmentCard title='Soft Fail' rules={rules.softFail} loading={loading} />
        <RulesAssessmentCard title='Hard Fail' rules={rules.hardFail} loading={loading} />
        <RulesAssessmentCard title='Passed' rules={rules.passed} loading={loading} />
        <RulesAssessmentCard title='Pending' rules={rules.pending} loading={loading} />
        {canEditCarrierStatus && (
          <Button variant='text' size='small' onClick={openModalHandler}>
            Submit a Decision
          </Button>
        )}
        <DetailsFromOtherSources />
      </Box>
      {isModalOpen && (
        <RulesManualReviewModal
          open={isModalOpen}
          onClose={closeModalHandler}
          allRules={allRules}
          rules={rules}
        />
      )}
    </Card>
  )
})

export default CarrierStatus
